import type {
  ApplicationDeviceRiskResponseDto,
  ApplicationJourneyHeaderDto,
  ApplicationVerificationSnapshotErrorDto,
} from '../api/admin';
import type { IdentityLookupRow } from '../api/identityVerificationDashboardApi';
import type { ApplicationTabKey } from './applicationDetailTabStatus';
import { declineReasonPlain } from './applicationJourneyCopy';
import {
  coerceIdentityRowsFromSessions,
  mergeIdentityRowsForOverviewAlerts,
  pickIdentityRowForOverviewAlerts,
  resolveApplicationOutcomes,
  resolveDecisionOutcomes,
  resolveDeviceOutcomes,
  resolveEmployerOutcomes,
  resolveIdentityOutcomes,
  resolveIncomeOutcomes,
  resolveLoanReviewOutcomes,
  resolveSnapshotErrorOutcomes,
  shouldShowOutcomeOnOverview,
  type ServiceOutcome,
} from './overviewServiceOutcomes';
import { resolveLinkageOverviewAlert } from './legacyLinkageDisplay';
import { overviewServiceTabTarget } from './overviewServiceTab';

export type OverviewAlertSeverity = 'error' | 'warning' | 'info';

export type OverviewServiceAlert = {
  id: string;
  /** Which service / domain raised it. */
  service: string;
  title: string;
  detail?: string | null;
  severity: OverviewAlertSeverity;
  /** Application-detail tab that owns this alert’s source of truth. */
  tabTarget: ApplicationTabKey | null;
};

export {
  coerceIdentityRowsFromSessions,
  mergeIdentityRowsForOverviewAlerts,
  pickIdentityRowForOverviewAlerts,
};

function pushUnique(out: OverviewServiceAlert[], alert: OverviewServiceAlert): void {
  if (out.some((a) => a.id === alert.id || (a.service === alert.service && a.title === alert.title))) {
    return;
  }
  out.push(alert);
}

function outcomeToAlert(outcome: ServiceOutcome): OverviewServiceAlert | null {
  if (!shouldShowOutcomeOnOverview(outcome)) return null;
  if (outcome.tone === 'success') return null;
  return {
    id: outcome.id,
    service: outcome.service,
    title: outcome.title,
    detail: outcome.detail,
    severity: outcome.tone,
    tabTarget: overviewServiceTabTarget(outcome.service),
  };
}

/**
 * Operator-facing flags from verification & decision services.
 *
 * Global rule: each service emits plain-English outcomes; Overview shows every
 * non-success outcome (fail / reject / review / multi-attempt recovery). Clean
 * success and quiet empty stay silent. New terminal statuses belong in the
 * service outcome resolver (e.g. identitySessionSummary), not new Overview ifs.
 */
export function buildApplicationOverviewAlerts(
  application: ApplicationJourneyHeaderDto,
  options?: {
    incomeRecords?: unknown | null;
    employerRecords?: unknown | null;
    deviceRisk?: ApplicationDeviceRiskResponseDto | null;
    /** @deprecated Prefer identityRows — first row alone often misses primary match. */
    identityRow?: IdentityLookupRow | null;
    /** All IV lookup attempts for this application (Identity tab source). */
    identityRows?: IdentityLookupRow[] | null;
    /**
     * Journey snapshot identity sessions (tab-dot source). Merged when lookup is
     * empty or missing status so Overview alerts match the Identity tab.
     */
    identitySessions?: unknown | null;
    snapshotErrors?: ApplicationVerificationSnapshotErrorDto[];
    employerFootprintStatus?: string | null;
    /** Persisted EV adminMessage / paystubs reason for footprint FAILED or READY+PAYSTUBS. */
    employerFootprintAdminMessage?: string | null;
    employerFootprintPaystubsReason?: string | null;
  },
): OverviewServiceAlert[] {
  const declinePlain = declineReasonPlain(application.declineReason);
  const declineRaw = application.declineReason ?? '';
  const declined = application.status.trim().toUpperCase() === 'DECLINED';

  const outcomes: ServiceOutcome[] = [
    ...resolveApplicationOutcomes(application),
    ...resolveDecisionOutcomes(application),
    ...resolveDeviceOutcomes(options?.deviceRisk),
    ...resolveIdentityOutcomes({
      identityRows: options?.identityRows,
      identityRow: options?.identityRow,
      identitySessions: options?.identitySessions,
      declined,
      declinePlain,
      declineRaw,
    }),
    ...resolveIncomeOutcomes({
      incomeRecords: options?.incomeRecords,
      lastActionResult: application.lastActionResult,
    }),
    ...resolveEmployerOutcomes({
      application,
      employerRecords: options?.employerRecords,
      employerFootprintStatus: options?.employerFootprintStatus,
      employerFootprintAdminMessage: options?.employerFootprintAdminMessage,
      employerFootprintPaystubsReason: options?.employerFootprintPaystubsReason,
    }),
    ...resolveLoanReviewOutcomes(application),
    ...resolveSnapshotErrorOutcomes(options?.snapshotErrors),
  ];

  const alerts: OverviewServiceAlert[] = [];
  for (const outcome of outcomes) {
    const alert = outcomeToAlert(outcome);
    if (alert) pushUnique(alerts, alert);
  }

  const linkageAlert = resolveLinkageOverviewAlert(application.legacyLinkage);
  if (linkageAlert) {
    pushUnique(alerts, linkageAlert);
  }

  const severityRank: Record<OverviewAlertSeverity, number> = {
    error: 0,
    warning: 1,
    info: 2,
  };
  return alerts.sort((a, b) => severityRank[a.severity] - severityRank[b.severity]).slice(0, 14);
}
